import { Badge, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IHeaderParams } from 'ag-grid-community';
import { useEffect, useRef, useState } from 'react';
import { getKeyValue } from '../../../utils';
import { getColorByIndex } from '../../../utils/themeUtils';
import { ColumnReadonlyIcon } from './ColumnReadonlyIcon';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface AgHeaderComponentProps<R> extends IHeaderParams<R> {
    indexOfBadgeColor?: number;
    readonlyReasons?: string[];
}

const SortIcon = ({ sortOrder }: { sortOrder: 'asc' | 'desc' | null }) => {
    if (sortOrder == 'asc') {
        return <ArrowUpwardIcon sx={{ height: '18px' }} />;
    }
    if (sortOrder == 'desc') {
        return <ArrowDownwardIcon sx={{ height: '18px' }} />;
    }
    return null;
};

export function AgHeaderComponent<R>(props: AgHeaderComponentProps<R>) {
    const colDef = props.column.getColDef();
    const [sortOrder, setSortOrder] = useState(null as 'asc' | 'desc' | null);

    const onSortRequested = () => {
        props.progressSort(true);
    };
    useEffect(() => {
        props.column.addEventListener('sortChanged', () => {
            const sortOrder = props.column.getSort();
            setSortOrder(sortOrder ?? null);
        });
    });
    const HeaderCell = () => (
        <Box style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
            <span>{colDef.headerName}</span>
            <Box sx={{ position: 'absolute', top: '6px', right: '5px' }}>
                <SortIcon sortOrder={sortOrder} />
            </Box>
            <Box sx={{ position: 'absolute', top: '2px', right: '5px' }}>
                <ColumnReadonlyIcon readonlyReasons={props.readonlyReasons} />
            </Box>
        </Box>
    );
    // option 1) tell the grid when you want to progress the sorting

    const BadgeHeaderCell = () => {
        const ref = useRef(null as HTMLDivElement | null);
        const theme = useTheme();
        useEffect(() => {
            if (ref?.current != null && props.indexOfBadgeColor != null) {
                ref.current.setAttribute('style', 'position:static;');
                if (ref.current.childElementCount > 0) {
                    for (let i = 0; i < ref.current.childElementCount; i++) {
                        const element = ref.current.children[i];
                        if (element.classList.contains('MuiBadge-badge')) {
                            const color = getKeyValue(getColorByIndex(props.indexOfBadgeColor))(
                                theme.palette
                            ).main;
                            const style = `right:10px;bottom:10px;background-color:${color}`;
                            element.setAttribute('style', style);
                        }
                    }
                }
            }
        }, [ref, theme.palette]);
        return (
            <Badge
                ref={ref}
                variant="dot"
                overlap="rectangular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <HeaderCell />
            </Badge>
        );
    };
    return (
        <Box
            sx={{
                cursor: 'move',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                padding: '5px 0px'
            }}
            component="div"
            onClick={() => onSortRequested()}
        >
            {props.indexOfBadgeColor != null ? <BadgeHeaderCell /> : <HeaderCell />}
        </Box>
    );
}
